/**
 * MaTourneePage — Vue agent terrain mobile-first, mode "Ma semaine".
 *
 * L13.16.6 — la tournée redevient une VUE D'ENSEMBLE.
 *   Avant : chaque créneau avait des boutons "Marquer terminé" /
 *   "Partiellement terminé" qui ouvraient une fenêtre permettant de clôturer
 *   SANS photo — un doublon dangereux avec la fiche.
 *   Maintenant : l'agent consulte sa semaine, et pour AGIR il ouvre la fiche
 *   du signalement (bouton "Accéder à la fiche"). C'est dans la fiche, et
 *   seulement là, qu'on prend les photos avant/après et qu'on clôture.
 *
 * Affichage :
 *   - 7 jours (lun → dim), navigation < / >
 *   - Pour chaque créneau : statut, heure, titre, et accès à la fiche liée
 *   - Les créneaux terminés ou annulés sont grisés
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import {
  Card, Badge, Alert,
} from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { ServiceTechniqueAgentTabs } from '@/pages/service-technique/ServiceTechniqueAgentTabs';
import {
  ChevronLeft, ChevronRight, Calendar,
  AlertCircle, CheckCircle2, RotateCcw, Clock, Loader2, FileText,
} from 'lucide-react';

// ---------- Types locaux (le shared-types dist n'a pas tous les champs L13.12) ----------
interface CreneauAgent {
  id: string;
  titre: string;
  description: string | null;
  dateDebut: string;
  dateFin: string;
  signalementId: string | null;
  signalementNumero: string | null;
  type: string;
  couleurMarker: string | null;
  statut: 'actif' | 'partiellement_termine' | 'termine' | 'annule';
  termineAt: string | null;
  termineCommentaire: string | null;
  estSuite: boolean;
}

const STATUT_LABELS: Record<string, string> = {
  actif: 'À faire',
  partiellement_termine: 'Partiellement fait',
  termine: 'Terminé',
  annule: 'Annulé',
};
const STATUT_VARIANTS: Record<string, 'neutral' | 'info' | 'success' | 'warning'> = {
  actif: 'info',
  partiellement_termine: 'warning',
  termine: 'success',
  annule: 'neutral',
};

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// ---------- Helpers semaine ----------

function getMondayOfWeek(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = x.getDay(); // 0=dim, 1=lun, ...
  const diff = dow === 0 ? -6 : 1 - dow;
  x.setDate(x.getDate() + diff);
  return x;
}

/**
 * L13.14 — Fix timezone (cf. R13.4 sur le planning manager).
 * `weekStart.toISOString()` convertit en UTC : minuit lundi heure de Paris
 * devient 22h dimanche UTC → `.slice(0,10)` renvoie le dimanche. Le backend
 * recule alors d'une semaine. On sérialise donc la date à partir de ses
 * composantes LOCALES, sans jamais passer par UTC.
 */
function toYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatWeekRange(start: Date): string {
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  return `Semaine du ${fmt(start)} au ${fmt(end)}`;
}

function groupByDay(items: CreneauAgent[], weekStart: Date): CreneauAgent[][] {
  const days: CreneauAgent[][] = [[], [], [], [], [], [], []];
  for (const c of items) {
    const d = new Date(c.dateDebut);
    const diffDays = Math.floor((d.getTime() - weekStart.getTime()) / 86400000);
    if (diffDays >= 0 && diffDays < 7) {
      days[diffDays]!.push(c);
    }
  }
  // Tri par heure dans chaque jour
  days.forEach((day) =>
    day.sort((a, b) => new Date(a.dateDebut).getTime() - new Date(b.dateDebut).getTime())
  );
  return days;
}

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

export function MaTourneePage() {

  const [weekStart, setWeekStart] = useState<Date>(() => getMondayOfWeek(new Date()));
  const [items, setItems] = useState<CreneauAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const iso = toYMD(weekStart); // L13.14 — date locale, jamais toISOString (UTC)
      const res = await api.serviceTechnique.planning.maSemaine(iso);
      setItems((res.items ?? []) as CreneauAgent[]);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { void reload(); }, [reload]);

  const days = useMemo(() => groupByDay(items, weekStart), [items, weekStart]);

  const counts = useMemo(() => {
    const actifs = items.filter((i) => i.statut === 'actif').length;
    const termines = items.filter((i) => i.statut === 'termine').length;
    const partiels = items.filter((i) => i.statut === 'partiellement_termine').length;
    return { actifs, termines, partiels, total: items.length };
  }, [items]);

  function shiftWeek(deltaDays: number) {
    const x = new Date(weekStart);
    x.setDate(x.getDate() + deltaDays);
    setWeekStart(x);
  }

  return (
    <div className="min-h-screen text-ink pb-12">
      <AppHeader
        module="Service technique"
        accent="tech"
        actions={
          <button
            type="button"
            onClick={() => void reload()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-r-md border border-border bg-surface/60 text-ink-muted backdrop-blur-glass transition-all hover:bg-surface/80 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            aria-label="Recharger"
            title="Recharger"
          >
            <RotateCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
          </button>
        }
      />
      <ServiceTechniqueAgentTabs active="planning" />

      <main className="mx-auto max-w-3xl px-4 py-5 space-y-4 animate-fade-in">
        {/* Navigation semaine */}
        <Card variant="glass" padded>
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => shiftWeek(-7)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-r-md border border-border bg-surface/60 text-ink-muted hover:bg-surface/80 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              aria-label="Semaine précédente"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <div className="flex-1 text-center">
              <div className="flex items-center justify-center gap-1.5 text-sm font-medium text-ink">
                <Calendar className="h-4 w-4 text-tech-fg" aria-hidden="true" />
                {formatWeekRange(weekStart)}
              </div>
              <button
                type="button"
                onClick={() => setWeekStart(getMondayOfWeek(new Date()))}
                className="mt-1 text-2xs text-ink-subtle hover:text-ink underline-offset-4 hover:underline"
              >
                Cette semaine
              </button>
            </div>
            <button
              type="button"
              onClick={() => shiftWeek(7)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-r-md border border-border bg-surface/60 text-ink-muted hover:bg-surface/80 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              aria-label="Semaine suivante"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </Card>

        {/* Compteurs */}
        <div className="grid grid-cols-3 gap-2">
          <Card variant="glass" padded>
            <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">À faire</div>
            <div className="mt-0.5 font-display text-2xl font-semibold text-info">{counts.actifs}</div>
          </Card>
          <Card variant="glass" padded>
            <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Terminés</div>
            <div className="mt-0.5 font-display text-2xl font-semibold text-success">{counts.termines}</div>
          </Card>
          <Card variant="glass" padded>
            <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Partiels</div>
            <div className="mt-0.5 font-display text-2xl font-semibold text-warning">{counts.partiels}</div>
          </Card>
        </div>

        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

        {loading && (
          <Card variant="glass" padded>
            <div className="flex items-center gap-2 text-sm text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Chargement de la semaine…
            </div>
          </Card>
        )}

        {!loading && counts.total === 0 && (
          <Card variant="glass" padded>
            <div className="text-center py-6">
              <CheckCircle2 className="mx-auto h-10 w-10 text-success" aria-hidden="true" />
              <h2 className="mt-3 font-display text-base font-semibold text-ink">Rien sur votre planning</h2>
              <p className="mt-1 text-sm text-ink-muted">Aucun créneau pour cette semaine.</p>
            </div>
          </Card>
        )}

        {!loading && counts.total > 0 && (
          <div className="space-y-3">
            {days.map((dayCreneaux, dayIdx) => {
              if (dayCreneaux.length === 0) return null;
              const dayDate = new Date(weekStart);
              dayDate.setDate(dayDate.getDate() + dayIdx);
              const isToday = (() => {
                const t = new Date();
                return dayDate.getFullYear() === t.getFullYear()
                  && dayDate.getMonth() === t.getMonth()
                  && dayDate.getDate() === t.getDate();
              })();
              return (
                <div key={dayIdx}>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <span className={`text-2xs font-semibold uppercase tracking-r-eyebrow ${isToday ? 'text-tech-fg' : 'text-ink-subtle'}`}>
                      {DAY_LABELS[dayIdx]} {dayDate.getDate()}
                    </span>
                    {isToday && (
                      <Badge variant="info" size="sm">Aujourd'hui</Badge>
                    )}
                    <span className="text-2xs text-ink-subtle">·  {dayCreneaux.length} créneau{dayCreneaux.length > 1 ? 'x' : ''}</span>
                  </div>
                  <div className="space-y-2">
                    {dayCreneaux.map((c) => (
                      <CreneauCard
                        key={c.id}
                        creneau={c}
                        onOpenSignalement={
                          c.signalementId
                            ? () => navigate(`/service-technique/signalements/${c.signalementId}`)
                            : undefined
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

// ============================================================================
// CreneauCard — carte de créneau (consultation + accès à la fiche)
// ============================================================================

function CreneauCard({
  creneau,
  onOpenSignalement,
}: {
  creneau: CreneauAgent;
  onOpenSignalement?: () => void;
}) {
  const debut = new Date(creneau.dateDebut);
  const fin = new Date(creneau.dateFin);
  const horaire = `${debut.getHours().toString().padStart(2, '0')}h${debut.getMinutes().toString().padStart(2, '0')} → ${fin.getHours().toString().padStart(2, '0')}h${fin.getMinutes().toString().padStart(2, '0')}`;
  // Un créneau terminé OU annulé est clôturé → grisé.
  const isClos = creneau.statut === 'termine' || creneau.statut === 'annule';

  return (
    <Card variant="glass" padded className={isClos ? 'opacity-60' : ''}>
      <div className="flex items-start gap-3">
        <div
          className="mt-0.5 h-2 w-2 flex-shrink-0 rounded-full"
          style={{ background: creneau.couleurMarker || 'oklch(var(--c-tech))' }}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant={STATUT_VARIANTS[creneau.statut] ?? 'neutral'} size="sm">
              {STATUT_LABELS[creneau.statut] ?? creneau.statut}
            </Badge>
            {creneau.estSuite && (
              <Badge variant="warning" size="sm" title="Reprise d'une intervention précédente partiellement faite">
                <RotateCcw className="h-3 w-3" aria-hidden="true" />
                Suite
              </Badge>
            )}
            <span className="inline-flex items-center gap-1 text-2xs text-ink-subtle tabular-nums">
              <Clock className="h-3 w-3" aria-hidden="true" />
              {horaire}
            </span>
          </div>
          <h3 className="mt-1.5 font-display text-base font-semibold tracking-r-snug text-ink line-clamp-2">
            {creneau.titre}
          </h3>
          {creneau.description && (
            <p className="mt-1 text-xs text-ink-muted line-clamp-2">{creneau.description}</p>
          )}

          {/* Accès à la fiche — c'est là que l'agent agit (photos, clôture). */}
          {creneau.signalementId && (
            <button
              type="button"
              onClick={onOpenSignalement}
              className="mt-3 flex w-full items-center justify-between gap-2 rounded-r-lg border border-tech/30 bg-tech-tint/40 px-3 py-2.5 text-sm font-medium text-tech-fg transition-all hover:bg-tech-tint/60 hover:border-tech/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            >
              <span className="inline-flex items-center gap-2">
                <FileText className="h-4 w-4" aria-hidden="true" />
                Accéder à la fiche
              </span>
              <span className="inline-flex items-center gap-1.5 min-w-0">
                {creneau.signalementNumero && (
                  <span className="font-mono text-2xs text-ink-muted truncate">
                    {creneau.signalementNumero}
                  </span>
                )}
                <ChevronRight className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              </span>
            </button>
          )}

          {creneau.statut === 'termine' && creneau.termineAt && (
            <p className="mt-2 text-2xs text-success">
              ✓ Terminé le {new Date(creneau.termineAt).toLocaleString('fr-FR')}
              {creneau.termineCommentaire && ` — ${creneau.termineCommentaire}`}
            </p>
          )}
          {creneau.statut === 'partiellement_termine' && creneau.termineCommentaire && (
            <p className="mt-2 text-2xs text-warning">
              ⚠ Partiellement fait : {creneau.termineCommentaire}
            </p>
          )}
          {creneau.statut === 'annule' && (
            <p className="mt-2 text-2xs text-ink-subtle">
              Créneau annulé{creneau.termineCommentaire ? ` — ${creneau.termineCommentaire}` : ''}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
